/**
 * NetApp Query Templates API Service
 */

import { apiClient } from '@/lib/api/client';

export interface QueryTemplate {
  task_name: string;
  query_template: string;
  description: string;
  updated_at: string | null;
}

export interface TemplateBackup {
  task_name: string;
  timestamp: string;
  filename: string;
  path: string;
}

export interface TemplateUpdateResponse {
  task_name: string;
  message: string;
  backup_created: boolean;
}

export interface RestoreResponse {
  task_name: string;
  message: string;
  query_template: string;
}

export interface SyncResponse {
  updated_count: number;
  updated_templates: string[];
  message: string;
}

class NetAppTemplatesAPI {
  /**
   * Get all query templates
   */
  async getTemplates(): Promise<QueryTemplate[]> {
    const response = await apiClient.get('/netapp/templates');
    return response.data;
  }

  /**
   * Get a specific query template
   */
  async getTemplate(taskName: string): Promise<QueryTemplate> {
    const response = await apiClient.get(`/netapp/templates/${taskName}`);
    return response.data;
  }

  /**
   * Update a query template (creates backup automatically)
   */
  async updateTemplate(
    taskName: string,
    queryTemplate: string
  ): Promise<TemplateUpdateResponse> {
    const response = await apiClient.put(`/netapp/templates/${taskName}`, {
      query_template: queryTemplate,
    });
    return response.data;
  }

  /**
   * List available backups for a query template
   */
  async getBackups(taskName: string): Promise<TemplateBackup[]> {
    const response = await apiClient.get(`/netapp/templates/${taskName}/backups`);
    return response.data;
  }

  /**
   * Restore a query template from a backup
   */
  async restoreFromBackup(
    taskName: string,
    backupFilename: string
  ): Promise<RestoreResponse> {
    const response = await apiClient.post(`/netapp/templates/${taskName}/restore`, {
      backup_filename: backupFilename,
    });
    return response.data;
  }

  /**
   * Delete a backup file
   */
  async deleteBackup(taskName: string, backupFilename: string): Promise<any> {
    const response = await apiClient.delete(
      `/netapp/templates/${taskName}/backups/${encodeURIComponent(backupFilename)}`
    );
    return response.data;
  }

  /**
   * Force sync templates from files to database
   */
  async syncTemplates(force: boolean = false): Promise<SyncResponse> {
    const response = await apiClient.post('/netapp/templates/sync', {
      force,
    });
    return response.data;
  }

  /**
   * Test a query template against NetApp API
   */
  async testQuery(taskName: string, customerId?: string): Promise<any> {
    const response = await apiClient.post(`/netapp/templates/${taskName}/test`, {
      customer_id: customerId,
    });
    return response.data;
  }
}

export const netappTemplatesAPI = new NetAppTemplatesAPI();